import { BuyingOffer } from '../domain/BuyingOffer';
import {
  CardNegotiationStatus,
  NegotiationOfferStatus,
  NegotiationOfferStatusPlusBuyerData,
} from '../domain/NegotiationOfferStatus';
import { SellingOffersNegotiationData } from '../domain/SellingOffersNegotiation';
import { NegotiationStatusDTO } from '../dtos/NegotiationOfferStatusDTO';
import { Negotiation } from '../negotiation';

export function mapSellingOffersNegotiationDataToNegotiationOfferStatus(
  data: SellingOffersNegotiationData,
  isSeller: boolean,
): NegotiationOfferStatusPlusBuyerData {
  if (!data) {
    throw new Error('Cannot map null or undefined data for SellingOffersNegotiationData');
  }
  return {
    id: String(data.id),
    isSeller: isSeller,
    isClosed: data.negotiationStatus.isClosed,
    isAccepted: data.negotiationStatus.isAccepted,
    sellerRemainingOffers: data.negotiationStatus.callerRemainingActionsNumber,
    buyerRemainingOffers: data.negotiationStatus.callerRemainingActionsNumber,
    userSubMessage: data.negotiationStatus.callerSubMessage,
    nextActions: data.negotiationStatus.callerPossibleActions,
    lastEvent: {
      createdBy: data.lastAction.originator,
      price: {
        formatted: data.lastAction.offerFormatted,
      },
      sellerPrice: {
        formatted: data.lastAction.offerSellerEarningsFormatted,
      },
      userMessage: data.lastAction.actionNote,
      action: data.lastAction.action,
    },
    buyer: {
      id: data.buyer.id,
      name: data.buyer.name,
      picturePath: data.buyer.picturePath,
    },
    hasBestOffer: data.hasBestOffer,
  };
}

export const mapNegotiationToNegotiationOfferStatus = (
  negotiation: Negotiation,
  isSeller: boolean,
): NegotiationOfferStatusPlusBuyerData => {
  if (!negotiation) {
    throw new Error('Cannot map null or undefined negotiation');
  }
  if (!negotiation.lastEvent) {
    throw new Error('Cannot map negotiation without lastEvent');
  }
  if (!negotiation.buyer) {
    throw new Error('Cannot map negotiation without buyer');
  }

  return {
    id: negotiation.id,
    isSeller: isSeller,
    isClosed: negotiation.isClosed,
    isAccepted: negotiation.isAccepted,
    userSubMessage: negotiation.userSubMessage,
    nextActions: negotiation.nextActions,
    sellerRemainingOffers: negotiation.sellerRemainingOffers,
    buyerRemainingOffers: negotiation.buyerRemainingOffers,
    lastEvent: {
      createdBy: negotiation.lastEvent.createdBy,
      price: {
        formatted: negotiation.lastEvent.price?.formatted,
      },
      sellerPrice: {
        formatted: negotiation.lastEvent.sellerPvnPrice?.formatted,
      },
      userMessage: negotiation.lastEvent.userMessage,
      action: negotiation.lastEvent.action,
    },
    buyer: {
      id: Number(negotiation.buyer.id),
      name: negotiation.buyer.firstname,
      picturePath: negotiation.buyer.picture.path,
    },
    hasBestOffer: false,
  };
};

export const mapBuyingOfferToNegotiationOfferStatus = (negotiation: BuyingOffer): NegotiationOfferStatus => {
  if (!negotiation) {
    throw new Error('Cannot map null or undefined negotiation');
  }
  if (!negotiation?.lastAction) {
    throw new Error('Cannot map negotiation without lastAction');
  }
  if (!negotiation.negotiationStatus) {
    throw new Error('Cannot map negotiation without negotiationStatus');
  }

  const { id, negotiationStatus, lastAction } = negotiation;

  return {
    id: id,
    isSeller: false,
    isClosed: negotiationStatus.isClosed,
    isAccepted: negotiationStatus.isAccepted,
    userSubMessage: negotiationStatus.callerSubMessage,
    nextActions: negotiationStatus.callerPossibleActions,
    sellerRemainingOffers: negotiationStatus.callerRemainingActionsNumber,
    buyerRemainingOffers: negotiationStatus.callerRemainingActionsNumber,
    lastEvent: {
      createdBy: lastAction.originator,
      price: {
        formatted: lastAction.offerFormatted,
      },
      userMessage: lastAction.actionNote,
      action: lastAction.action,
    },
    hasBestOffer: false,
  };
};

/**
 * Maps a NegotiationStatusDTO to a NegotiationStatus domain type
 */
export const mapNegotiationStatusFromDTO = (dto: NegotiationStatusDTO): CardNegotiationStatus => {
  return {
    callerSubMessage: dto.callerSubMessage,
    isAccepted: dto.isAccepted,
    isClosed: dto.isClosed,
    callerRemainingActionsNumber: dto.callerRemainingActionsNumber,
    callerPossibleActions: [...dto.callerPossibleActions],
    callerMessage: dto.callerMessage ?? '',
  };
};
